export const DEFAULT_TZ_OFFSET_MINUTES = 7 * 60;

const normalizeHour = (hour = 0) => {
  const safeHour = Number.isFinite(hour) ? hour : 0;
  return ((safeHour % 24) + 24) % 24;
};

export const normalizeTimezoneOffset = (timezoneOffsetMinutes) => (
  Number.isFinite(timezoneOffsetMinutes) ? timezoneOffsetMinutes : DEFAULT_TZ_OFFSET_MINUTES
);

const getDatePartsFromUtcDate = (date) => ({
  year: date.getUTCFullYear(),
  month: date.getUTCMonth(),
  day: date.getUTCDate()
});

const resolveDateParts = (dateInput) => {
  if (dateInput instanceof Date) {
    return getDatePartsFromUtcDate(dateInput);
  }

  const { year, month, day } = dateInput || {};
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    throw new Error('dateInput khong hop le');
  }
  return { year, month, day };
};

export const toUtcFromLocalTime = (
  dateInput,
  hour = 0,
  timezoneOffsetMinutes = DEFAULT_TZ_OFFSET_MINUTES
) => {
  const { year, month, day } = resolveDateParts(dateInput);
  const normalizedHour = normalizeHour(hour);
  const offsetMinutes = normalizeTimezoneOffset(timezoneOffsetMinutes);
  // `Date.UTC` cắt bỏ phần thập phân của giờ. Cộng milliseconds từ đầu ngày để
  // giữ chính xác phút/giây, đặc biệt tại ranh giới tiết khí như 17:11.
  return Date.UTC(year, month, day) + normalizedHour * 60 * 60 * 1000 - offsetMinutes * 60 * 1000;
};

export const toUtcDateFromLocal = (dateInput, hour = 0, timezoneOffsetMinutes = DEFAULT_TZ_OFFSET_MINUTES) => {
  const utcMs = toUtcFromLocalTime(dateInput, hour, timezoneOffsetMinutes);
  const utcDate = new Date(utcMs);
  return new Date(Date.UTC(
    utcDate.getUTCFullYear(),
    utcDate.getUTCMonth(),
    utcDate.getUTCDate()
  ));
};

export const formatLocalDateTime = (dateInput, timezoneOffsetMinutes = DEFAULT_TZ_OFFSET_MINUTES) => {
  if (!(dateInput instanceof Date)) return null;
  const offsetMinutes = normalizeTimezoneOffset(timezoneOffsetMinutes);
  const localMs = dateInput.getTime() + offsetMinutes * 60 * 1000;
  const localDate = new Date(localMs);
  const pad = (value) => `${Math.trunc(Math.abs(value))}`.padStart(2, '0');
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
  const offsetMins = Math.abs(offsetMinutes) % 60;

  const datePart = `${localDate.getUTCFullYear()}-${pad(localDate.getUTCMonth() + 1)}-${pad(localDate.getUTCDate())}`;
  const timePart = `${pad(localDate.getUTCHours())}:${pad(localDate.getUTCMinutes())}:${pad(localDate.getUTCSeconds())}`;
  const offsetPart = `${sign}${pad(offsetHours)}:${pad(offsetMins)}`;

  // Return a single ISO-like string (YYYY-MM-DDTHH:mm:ss±HH:mm) so downstream parsers
  // handle solar-term and birth timestamps without the extra "T" that previously broke Date parsing.
  return `${datePart}T${timePart}${offsetPart}`;
};

export const addDaysToDateParts = (dateInput, days) => {
  const { year, month, day } = resolveDateParts(dateInput);
  const date = new Date(Date.UTC(year, month, day));
  date.setUTCDate(date.getUTCDate() + days);
  return getDatePartsFromUtcDate(date);
};

export const getDateParts = (dateInput) => resolveDateParts(dateInput);
